import React, { useMemo } from 'react';
import { TileToolbar } from 'widgets/tiles/components/tile/TileToolbar';
import { SymbolFromConfigOrPortfolioLinkedStatus } from 'portfolio/components/SymbolFromConfigOrPortfolioLinkedStatus';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { WidgetTile } from 'widgets/tiles/components/tile/WidgetTile';
import { usePerfVsIndexCapsuleConfigSlice } from '../store/config/configslice';
import { PerfVsIndexCapsuleContent } from './PerfVsIndexCapsuleContent';
import PerfVsIndexCapsuleSettings, {
    PerfVsIndexCapsuleSettingsText,
} from './settings/PerfVsIndexCapsuleSettings';
import PerfVsIndexCapsuleChartHelp from './chart/PerfVsIndexCapsuleChartHelp';
import classNames from 'classnames';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';

import { useWidgetContext } from 'widgets/WidgetContext';
import { useCapsuleBoundSymbol } from 'widgets/tiles/components/tile/hooks';

import { StreamMessage } from 'utils/messages/StreamMessage';
import { buildPerfVsIndexData } from 'widgets/perfVsIndexCapsule/perfVsIndexCapsuleData';
import './PerfVsIndexCapsule.scss';
import { extractOrderStatData } from 'widgets/fillsGraph/fillsGraphData';
import { useFillsGraphStreamedData } from 'widgets/fillsGraph/store/streamedData/hooks';
import { useFetchIndexMappingsUserPreference } from 'symbols/indexMappings/hooks';
import {
    useExternalMarketBarData,
    useExternalMarketBarDataMessages,
    useExternalMarketDataRefData,
} from 'marketData/hooks';
import {
    useFillsGraphDataIndexDescription,
    useFillsGraphDataIndexUserSymbology,
} from 'widgets/fillsGraph/store/data/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

export function PerfVsIndexCapsule() {
    const { config, dispatchConfigChanged } =
        usePerfVsIndexCapsuleConfigSlice();
    const { showDataFor, symbolInTitle } = config;

    const { symbol, symbology } = useCapsuleBoundSymbol();

    useFetchIndexMappingsUserPreference();

    const { streamedData, streamKey } = useFillsGraphStreamedData(
        symbol,
        config.indexFallback
    );

    const streamData = streamedData?.data;
    const orderStatData = streamData?.orderStat;

    const indexDescription = useFillsGraphDataIndexDescription(streamData);
    const indexInUsersSymbology =
        useFillsGraphDataIndexUserSymbology(streamData);

    const inetIndexSym = streamData?.orderStat?.indexSym;
    const index =
        symbolInTitle && indexInUsersSymbology
            ? indexInUsersSymbology
            : symbol && indexDescription
            ? indexDescription
            : '';

    const symbolMarketData = useExternalMarketBarData(symbol, symbology, 1);
    const symbolMarketRefData = useExternalMarketDataRefData(symbol, symbology);
    const indexMarketData = useExternalMarketBarData(
        inetIndexSym,
        SymbologyValues.Instinet,
        1
    );
    const indexMarketRefData = useExternalMarketDataRefData(
        inetIndexSym,
        symbology
    );
    const symbolMarketDataMessages = useExternalMarketBarDataMessages(
        symbol,
        symbology,
        1
    );
    const indexMarketDataMessages = useExternalMarketBarDataMessages(
        inetIndexSym,
        SymbologyValues.Instinet,
        1
    );

    const widgetMessages = [
        ...(streamedData?.messages ?? []),
        ...symbolMarketDataMessages,
        ...indexMarketDataMessages,
    ];

    const data = buildPerfVsIndexData({
        streamData,
        showDataFor,
        dapiPriceData: symbolMarketData,
        dapiRefData: symbolMarketRefData,
        dapiIndexPriceData: indexMarketData,
        dapiIndexRefData: indexMarketRefData,
        orderStatData: extractOrderStatData(orderStatData),
    });

    const { widgetKey } = useWidgetContext();

    const messages: StreamMessage[] = [...widgetMessages];

    useWidgetStream();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const warning = useMemo(() => <SymbolWarning />, [config]);

    const cssClasses = classNames('PerfVsIndexCapsule');

    const title = `Perf vs ${index?.length > 0 ? index : 'Index'}`;

    return (
        <WidgetTile className={cssClasses} data-stream-key={streamKey}>
            <TileToolbar
                title={title}
                settingsBody={<PerfVsIndexCapsuleSettings />}
                settingsHeader={PerfVsIndexCapsuleSettingsText}
                preceedingItems={<PerfVsIndexCapsuleChartHelp />}
                messages={messages}
                settingsClassName={'PerfVsIndexCapsuleSettingsModal'}
            />

            <div className="capsule-body">
                <SymbolFromConfigOrPortfolioLinkedStatus warning={warning}>
                    <PerfVsIndexCapsuleContent
                        data={data}
                        config={config}
                        index={index}
                        dispatchConfigChanged={dispatchConfigChanged}
                        symbol={symbol}
                        capsuleKey={widgetKey}
                    />
                </SymbolFromConfigOrPortfolioLinkedStatus>
            </div>
        </WidgetTile>
    );

    function SymbolWarning() {
        return (
            <span>
                Select a symbol or specify one in the&nbsp;
                <ConfigSettingsModal
                    dispatcher={dispatchConfigChanged}
                    headerText={PerfVsIndexCapsuleSettingsText}
                    triggerText={'Capsule Settings'}
                    renderSettings={(p) => (
                        <PerfVsIndexCapsuleSettings {...p} />
                    )}
                />
                .
            </span>
        );
    }
}
